import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCog } from 'react-icons/fa';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import type { U340xKpiSeriesPoint, U340xKpiTileStatus } from '../types/u340xKpiTypes';
import {
  U340X_KPI_MAX_MONTHS_STANDALONE,
  U340X_KPI_MIN_MONTHS,
} from '../types/u340xKpiTypes';
import type {
  U340xKpiDetailColumnDef,
  U340xKpiMonthDetailRow,
  U340xKpiMonthDetailTotals,
} from '../types/u340xKpiLuTypes';
import type { U340xKpiMetricConfig } from '../types/u340xKpiTypes';
import { NS } from '../u340xKpiNs';
import U340xKpiMonthDetailTable, {
  type U340xKpiMonthDetailTableStatus,
} from './U340xKpiMonthDetailTable';
import U340xKpiMonthTimeline from './U340xKpiMonthTimeline';
import U340xKpiMonthsConfigDialog from './U340xKpiMonthsConfigDialog';
import U340xKpiTile from './U340xKpiTile';

export interface U340xKpiStandaloneScreenProps {
  title: string;
  series: U340xKpiSeriesPoint[];
  primaryMetric: U340xKpiMetricConfig & { axis: 'left' };
  secondaryMetric: U340xKpiMetricConfig & { axis: 'right' };
  primaryStackMetric?: U340xKpiMetricConfig & { axis: 'left' };
  secondaryExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  months: number;
  anchorMonth: string;
  seriesStatus: U340xKpiTileStatus;
  seriesErrorMessage?: string;
  selectedMonth: string | null;
  detailColumns: U340xKpiDetailColumnDef[];
  detailRows: U340xKpiMonthDetailRow[];
  detailStatus: U340xKpiMonthDetailTableStatus;
  detailErrorMessage?: string;
  detailTotals?: U340xKpiMonthDetailTotals | null;
  onMonthsChange: (months: number) => void;
  onAnchorMonthChange: (anchorMonth: string) => void;
  onMonthClick: (month: string) => void;
}

function U340xKpiStandaloneScreen({
  title,
  series,
  primaryMetric,
  secondaryMetric,
  primaryStackMetric,
  secondaryExtraMetric,
  secondaryTecMetric,
  secondaryTecExtraMetric,
  months,
  anchorMonth,
  seriesStatus,
  seriesErrorMessage,
  selectedMonth,
  detailColumns,
  detailRows,
  detailStatus,
  detailErrorMessage,
  detailTotals,
  onMonthsChange,
  onAnchorMonthChange,
  onMonthClick,
}: U340xKpiStandaloneScreenProps) {
  const { t } = useTranslation(NS);
  const [monthsConfigOpen, setMonthsConfigOpen] = useState(false);

  return (
    <div className="flex w-full flex-col gap-5 px-4 py-4 sm:px-6">
      <U340xKpiTile
        title={title}
        size="large"
        series={series}
        primaryMetric={primaryMetric}
        secondaryMetric={secondaryMetric}
        primaryStackMetric={primaryStackMetric}
        secondaryExtraMetric={secondaryExtraMetric}
        secondaryTecMetric={secondaryTecMetric}
        secondaryTecExtraMetric={secondaryTecExtraMetric}
        months={months}
        maxMonths={U340X_KPI_MAX_MONTHS_STANDALONE}
        minMonths={U340X_KPI_MIN_MONTHS}
        status={seriesStatus}
        errorMessage={seriesErrorMessage}
        onMonthClick={onMonthClick}
        selectedMonth={selectedMonth}
        showMonthsConfig={false}
        timeline={
          <U340xKpiMonthTimeline
            value={anchorMonth}
            windowMonths={months}
            onChange={onAnchorMonthChange}
            disabled={seriesStatus === 'loading'}
            headerActions={
              <U2000ActionBtn
                label={t('config.open')}
                icon={FaCog}
                variant="secondary"
                className="px-2.5! py-1! text-xs"
                title={t('config.openTitle', { months })}
                handleClick={() => setMonthsConfigOpen(true)}
              />
            }
          />
        }
      />

      <U340xKpiMonthsConfigDialog
        isOpen={monthsConfigOpen}
        months={months}
        maxMonths={U340X_KPI_MAX_MONTHS_STANDALONE}
        minMonths={U340X_KPI_MIN_MONTHS}
        onClose={() => setMonthsConfigOpen(false)}
        onSave={(next) => {
          setMonthsConfigOpen(false);
          onMonthsChange(next);
        }}
      />

      <U340xKpiMonthDetailTable
        month={selectedMonth}
        columns={detailColumns}
        rows={detailRows}
        status={detailStatus}
        errorMessage={detailErrorMessage}
        totals={detailTotals}
      />
    </div>
  );
}

export default U340xKpiStandaloneScreen;
